// MasterDashboardAvatar — Feb 2026 (Randy). Circular avatar with a
// long-press flow: upload from device OR AI-generate via silo-image-gen.
// Once saved, propagates everywhere the avatar is referenced (via the
// avatar_id URL bump).
import { useEffect, useRef, useState } from "react";
import { Sparkle, UploadSimple, X, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function MasterDashboardAvatar({ siloId, member, size = 64, interactive = true, onUpdated }) {
  const [popupOpen, setPopupOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState("");
  const fileRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  // Fetch the avatar as an authenticated blob so we can render <img> with
  // no query-string token. Re-runs when avatar_id changes.
  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    if (!member.avatar_id) { setBlobUrl(null); return; }
    (async () => {
      try {
        const r = await api.get(`/calendar/${siloId}/avatar/${encodeURIComponent(member.target_id)}`,
          { responseType: "blob" });
        if (cancelled) return;
        currentUrl = URL.createObjectURL(r.data);
        setBlobUrl(currentUrl);
      } catch { if (!cancelled) setBlobUrl(null); }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [siloId, member.target_id, member.avatar_id]);
  let pressTimer = null;
  const onDown = () => {
    if (!interactive) return;
    pressTimer = setTimeout(() => setPopupOpen(true), 550);
  };
  const onUp = () => { if (pressTimer) clearTimeout(pressTimer); };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const reader = new FileReader();
      const data_url = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/calendar/${siloId}/avatar/${encodeURIComponent(member.target_id)}`, { data_url });
      toast.success("Avatar updated.");
      setPopupOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that avatar.");
    } finally { setBusy(false); }
  };
  const generate = async () => {
    const p = prompt.trim();
    if (!p) { toast.error("Describe what you want first."); return; }
    setBusy(true);
    try {
      await api.post(`/calendar/${siloId}/avatar/${encodeURIComponent(member.target_id)}`, { ai_prompt: p });
      toast.success("AI avatar generated.");
      setPopupOpen(false); setPrompt("");
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate that avatar.");
    } finally { setBusy(false); }
  };
  const circleSize = { width: size, height: size };
  return (
    <>
      <button onMouseDown={onDown} onMouseUp={onUp} onTouchStart={onDown} onTouchEnd={onUp}
        onClick={() => interactive && setPopupOpen(true)}
        style={circleSize}
        data-testid={`master-dashboard-avatar-${member.target_id}`}
        className={`relative rounded-full overflow-hidden shrink-0 border border-white/15 bg-gradient-to-br from-white/10 to-white/2 flex items-center justify-center ${interactive ? "hover:border-white/40 cursor-pointer" : ""}`}>
        {blobUrl ? (
          <img src={blobUrl} alt={member.first_name} className="w-full h-full object-cover" />
        ) : (
          <span style={{ fontSize: size * 0.5 }} aria-hidden>{member.emoji || "👤"}</span>
        )}
      </button>
      {popupOpen && (
        <div className="fixed inset-0 z-[280] bg-black/70 flex items-center justify-center p-6" onClick={() => !busy && setPopupOpen(false)}>
          <div className="modal-safe bg-[#12151A] border border-white/10 rounded-3xl w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()} data-testid="master-dashboard-avatar-popup">
            <button onClick={() => !busy && setPopupOpen(false)} className="absolute top-4 right-4 p-1.5 rounded text-white/50 hover:text-white hover:bg-white/5">
              <X size={16} />
            </button>
            <div className="font-serif-display text-xl mb-1 text-white">Change avatar</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/40 mb-6">for {member.first_name}</div>
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="master-dashboard-avatar-upload"
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white text-sm mb-3">
              <UploadSimple size={16} weight="duotone" /> Upload a photo from this device
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="master-dashboard-avatar-file" />
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/25 my-3 text-center">or generate with AI</div>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
              placeholder="e.g. skateboarder teen with headphones, warm earthy tones, minimal linework"
              data-testid="master-dashboard-avatar-prompt"
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-white/25 focus:outline-none focus:border-white/25 mb-3" />
            <button onClick={generate} disabled={busy || !prompt.trim()} data-testid="master-dashboard-avatar-generate"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white text-black text-sm font-medium disabled:opacity-40">
              {busy ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="duotone" />}
              {busy ? "Working…" : "Generate with AI"}
            </button>
            <div className="text-[10px] text-white/30 mt-4 leading-relaxed">
              Uses your Gemini key (same as silo art). Saved avatars appear everywhere this person is shown, including the Add-to-Home-Screen icon library.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
